<template>
  <div class="relative min-h-hero lg:flex lg:flex-col pb-8 lg:pb-7 bg-paint">
    <transition name="fade">
      <CoatingColourSelector
        v-if="showSelector"
        :palettes="palettes"
        :product="product"
        :selected-colour="selectedColour"
        @select-colour="handleSelectColour"
      />
    </transition>
    <div class="relative flex-1 lg:flex lg:items-center lg:justify-start">
      <div
        class="lg:absolute lg:inset-0 flex items-center justify-center lg:pt-smh z-10"
      >
        <div class="w-4/5 sm:w-1/2 lg:w-1/3 pt-8 lg:pt-shh">
          <div
            class="relative h-0 pt-full rounded-full bg-grey overflow-hidden"
          >
            <template v-for="(palette, index) in palettes">
              <template v-for="(item, index) in palette.colours">
                <img
                  v-show="item.id === selectedColour.id"
                  class="absolute inset-0 w-full object-cover"
                  :src="item.image[0].url"
                />
              </template>
            </template>
          </div>
        </div>
      </div>
      <div class="relative px-ogs-large z-20">
        <div
          class="flex flex-col items-center lg:items-start pt-8 py-28 lg:pt-10 lg:pb-10"
        >
          <label class="text-xxs text-theme font-semibold uppercase mb-1"
            >Selected Stain</label
          >
          <div class="relative mb-5">
            <transition name="fade">
              <div
                v-if="isLoading"
                role="alert"
                data-testid="loading-message"
                class="absolute inset-0 flex items-center justify-center z-20"
              >
                <LoaderSpinner class="w-10 h-10 text-theme" />
              </div>
            </transition>
            <div
              v-if="coloursCount > 1"
              :class="{ 'opacity-25': isLoading, 'opacity-100': !isLoading }"
              class="relative transition-opacity duration-300 z-10"
            >
              <button
                class="link flex items-center border-b border-theme py-2 space-x-2"
                @click="showSelector = true"
              >
                <div class="text-xl text-theme">{{ selectedColour.title }}</div>
                <div class="caret bg-theme" aria-hidden="true"></div>
              </button>
            </div>
            <div
              v-else
              class="text-xl text-theme py-2"
              :class="{ 'opacity-25': isLoading, 'opacity-100': !isLoading }"
            >
              {{ selectedColour.title }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="relative grid grid-cols-12 gap-x-gs gap-y-8 px-ogs-large lg:py-7 z-20"
    >
      <div class="col-span-12 lg:col-span-4 space-y-2 text-center lg:text-left">
        <div class="max-w-sm lg:max-w-full mx-auto">
          <h1 class="text-xl text-theme leading-snug">
            {{ product.title }}
          </h1>
          <p class="text-theme text-theme text-sm md:text-base">
            {{ product.description }}
          </p>
        </div>
      </div>
      <form
        id="variantForm"
        name="variantForm"
        class="col-span-12 lg:col-span-8 lg:flex lg:items-end lg:justify-end"
        @submit="handleSubmit"
      >
        <ProductVariantSelector
          class="col-span-12 lg:col-span-8 lg:flex lg:items-end lg:justify-end lg:space-x-10"
          :sheens="variantSheens"
          :sizes="variantSizes"
          :selected-sheen="selectedSheen"
          :selected-size="selectedSize"
          :open-above="true"
          @change-sheen="selectedSheen = $event"
          @change-size="selectedSize = $event"
        />
      </form>
    </div>
    <div
      class="relative grid grid-cols-12 gap-x-gs gap-y-4 px-ogs-large pt-8 lg:pt-7 lg:border-t lg:border-theme z-20"
    >
      <div class="col-span-12 lg:col-span-6 xl:col-span-4 flex items-center">
        <button
          class="w-full lg:w-auto btn-soft"
          @click="$store.commit('openCoatingCalculator')"
        >
          Oil Calculator
        </button>
      </div>
      <div class="col-span-4 hidden xl:flex items-center justify-center">
        <h4 class="text-theme opacity-70 leading-tight text-center">
          Stain: {{ selectedColour.title }}
          {{ selectedSheen && ` / ${selectedSheen.title}` }}
          {{ selectedSize && ` / ${selectedSize.title}` }}
        </h4>
      </div>
      <div v-if="testPotProductId" class="col-span-12 lg:hidden">
        <ProductCoatingTestPot
          class="w-full btn-soft"
          :loading="isLoading"
          :colour-title="selectedColour.title"
          :test-pot="testPotSelected"
          :sku="variantSelected ? variantSelected.sku : ''"
          :product-title="product.title"
          :sheen="selectedSheen.title"
          :coating-colour="selectedColour.thumbnail.url"
          source="coating_product_page"
          >Test Pot - ${{
            testPotSelected ? testPotSelected.salePrice : 6
          }}</ProductCoatingTestPot
        >
      </div>
      <div
        class="col-span-12 lg:col-span-6 xl:col-span-4 lg:flex lg:items-center lg:justify-end lg:space-x-3"
      >
        <div v-if="testPotProductId" class="hidden lg:block">
          <ProductCoatingTestPot
            class="btn-soft"
            :loading="isLoading"
            :colour-title="selectedColour.title"
            :test-pot="testPotSelected"
            :sku="variantSelected ? variantSelected.sku : ''"
            :product-title="product.title"
            :sheen="selectedSheen.title"
            :coating-colour="selectedColour.thumbnail.url"
            source="coating_product_page"
            >Test Pot - ${{
              testPotSelected ? testPotSelected.salePrice : 6
            }}</ProductCoatingTestPot
          >
        </div>
        <ProductAddToCartForm
          form="variantForm"
          :variant="variantSelected"
          :loading="isLoading"
        />
      </div>
    </div>
    <Portal selector="#portal-target">
      <CoatingCalculator
        :coating="product"
        :is-visible="$store.state.ui.coatingCalculatorIsOpen"
        @closed="$store.commit('closeCoatingCalculator')"
      />
    </Portal>
  </div>
</template>
<script>
import { Portal } from "@linusborg/vue-simple-portal";
import api from "../../js/apiClient";
import CoatingCalculator from "../QuantityCalculator/CoatingCalculator.vue";
import CoatingColourSelector from "./CoatingColourSelector.vue";
import ProductAddToCartForm from "./ProductAddToCartForm.vue";
import ProductVariantSelector from "./ProductVariantSelector.vue";
import ProductCoatingTestPot from "./TestPotAddToCart.vue";
import ProductMixin from "./ProductMixin";
import LoaderSpinner from "../ui/LoaderSpinner.vue";

export default {
  name: "ProductCoating",

  components: {
    CoatingCalculator,
    CoatingColourSelector,
    ProductAddToCartForm,
    ProductVariantSelector,
    ProductCoatingTestPot,
    LoaderSpinner,
    Portal,
  },

  mixins: [ProductMixin],

  data() {
    return {
      showSelector: false,
      variantDetails: [],
      testPotVariants: [],
    };
  },

  computed: {
    coloursCount() {
      // total up number of colours in palettes

      return this.palettes.reduce((acc, variant) => {
        return acc + variant.colours.length;
      }, 0);
    },
    variantSheens() {
      const sheens = this.variantDetails.map((detail) => {
        return {
          id: parseInt(detail.id),
          title: detail.title,
          slug: detail.slug,
        };
      });

      return sheens || [];
    },

    variantSizes() {
      const sheen = this.variantDetails.filter((sheen) => {
        return sheen.id === this.selectedSheen.id;
      });

      // JANK ALERT: Ordering the sizes on title
      return (
        sheen[0]?.sizes.sort(function (a, b) {
          if (parseInt(a.title) < parseInt(b.title)) {
            return -1;
          }
          if (parseInt(b.title) < parseInt(a.title)) {
            return 1;
          }
          return 0;
        }) || []
      );
    },

    variantSelected() {
      if (!this.variantDetails.length) return {};
      const selectedSheen = this.variantDetails.filter(
        (variant) => variant.title === this.selectedSheen.title
      );
      const selectedSize = selectedSheen[0]?.sizes.filter(
        (size) => size.title == this.selectedSize.title
      );
      return selectedSize ? selectedSize[0].variant : {};
    },

    testPotSelected() {
      if (!this.testPotVariants.length) return {};
      const selectedTestPot = this.testPotVariants.filter(
        (testPot) =>
          !testPot.coatingSheen.length ||
          parseInt(testPot.coatingSheen[0].id) ===
            parseInt(this.selectedSheen.id)
      );

      return selectedTestPot[0];
    },
  },

  watch: {
    showSelector(value) {
      this.$store.commit("toggleSelector", value);
    },
  },

  methods: {
    /**
     * Handles the validation of the size select when adding a product to cart
     * Shows HTML 5 validation if no size has benn chosen
     * @author Daniel Siemers <daniel@batch.nz>
     * @param e Form submit event
     * @return void
     */
    handleSubmit(e) {
      e.preventDefault();
      e.target.reportValidity();
    },

    handleSelectColour(colour) {
      this.selectedColour = colour;
      this.showSelector = false;
    },

    async getQueryIds(queryBody) {
      this.isLoading = true;
      let ids = null;

      try {
        const requestOptions = {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ productId: this.product.id, ...queryBody }),
        };

        ids = await fetch(
          "/actions/url-query-handler/coating-query-string",
          requestOptions
        )
          .then((response) => response.json())
          .then((result) => result)
          .catch((error) => console.error("error", error));
      } catch (err) {
        // eslint-disable-next-line
        console.error(err);
      }

      this.isLoading = false;
      return ids;
    },

    /**
     * Gets the variants for the product with the selected colour and resets the
     * selected size
     * @author Daniel Siemers <daniel@batch.nz>
     * @return void
     */
    async getData() {
      this.isLoading = true;
      try {
        const { variants } = await api.variants.queryCoatingVariants(
          this.product.id,
          this.selectedColour.id
        );

        // Formats the data into a tree structure
        this.formatData(variants);
      } catch (err) {
        // eslint-disable-next-line
        console.error(err);
        this.variantDetails = null;
      }

      this.resetSize();

      // Make api call for coating test pots
      try {
        const testPot = await api.variants.queryCoatingTestPots(
          this.testPotProductId,
          this.selectedColour.id
        );

        this.testPotVariants = testPot.variants;

        this.isLoading = false;
      } catch (err) {
        // eslint-disable-next-line
        console.error(err);
        this.variantDetails = null;
        this.isLoading = false;
      }
    },

    /**
     * Formats the data from the query into a tree structure with sheen
     * name at the highest level, then size, then variant
     * @author Daniel Siemers <daniel@batch.nz>
     * @param variants Variants from the query
     * @return void
     */
    formatData(variants) {
      const details = [];

      variants.map((variant, index) => {
        // Resets the selected sheen
        this.selectedSheen =
          index === 0
            ? {
                id: parseInt(variant.coatingSheen[0].id),
                title: variant.coatingSheen[0].title,
                slug: variant.coatingSheen[0].slug,
              }
            : this.selectedSheen;

        for (const sheen of details) {
          // Add the size and variant to the sheen if it is already present in the array
          if (sheen.title === variant.coatingSheen[0].title) {
            sheen.sizes.push({
              id: parseInt(variant.size[0].id),
              title: variant.size[0].title,
              slug: variant.size[0].slug,
              variant,
            });

            return;
          }
        }

        // Add the sheen size and variant if the sheen is not already present in the array
        details.push({
          id: parseInt(variant.coatingSheen[0].id),
          title: variant.coatingSheen[0].title,
          sizes: [
            {
              id: parseInt(variant.size[0].id),
              title: variant.size[0].title,
              slug: variant.size[0].slug,
              variant,
            },
          ],
        });
      });

      this.variantDetails = details;
    },

    changeSheen() {
      this.resetSize();
    },

    /**
     * Resets the size selected
     * @author Daniel Siemers <daniel@batch.nz>
     * @return void
     */
    resetSize() {
      if (
        !this.variantSizes.some(
          (variantSize) => variantSize.id === this.selectedSize.id
        )
      ) {
        this.selectedSize = this.variantSizes[0];
      }
    },
  },
};
</script>
